import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { MapPin, Truck, Clock, Loader2, CheckCircle2, Package } from "lucide-react";

export default function PickupTrackPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api.get(`/pickup-drop/track/${token}`).then((r) => setData(r.data)).catch((e) => setErr(apiErr(e))).finally(() => setBusy(false));
  }, [token]);

  const curIdx = data ? data.statuses.findIndex((x) => x.key === data.status) : -1;

  return (
    <div className="min-h-screen bg-background" data-testid="pickup-track-page">
      <header className="h-16 border-b border-border bg-card sticky top-0 z-20 flex items-center justify-between px-6">
        <Link to="/"><Logo className="h-9" /></Link>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link to="/" className="text-sm font-bold uppercase tracking-wide border border-border px-4 py-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">Book a pickup</Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2 flex items-center gap-2"><Package className="h-4 w-4" /> Pickup & Drop</div>
        <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight leading-[0.95]">Track your order.</h1>

        {busy && <div className="mt-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {err && <div className="mt-8 border border-destructive/40 bg-destructive/5 text-destructive p-4 text-sm" data-testid="pickup-track-error">{err}</div>}

        {data && (
          <div className="mt-8 space-y-6" data-testid="pickup-track-result">
            <div className="border border-border bg-card p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Booking ID</div>
                  <div className="font-mono-num font-bold text-primary text-lg" data-testid="pickup-track-id">{data.booking_id}</div>
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 text-sm font-bold uppercase tracking-wide" data-testid="pickup-track-status">
                    <Truck className="h-4 w-4" /> {data.status_label}
                  </div>
                  {data.estimated_delivery_date && <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1 justify-end"><Clock className="h-3 w-3" /> ETA: <span className="font-medium text-foreground">by {data.estimated_delivery_date}</span></div>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm mt-6">
                <div className="border border-border p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Pickup</div>{data.pickup || "—"}</div>
                <div className="border border-border p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="h-3 w-3 text-primary" /> Delivery</div>{data.delivery || "—"}</div>
              </div>
              {data.cost != null && <div className="mt-4 text-sm text-muted-foreground">Amount paid: <span className="font-mono-num font-bold text-foreground">₹{Number(data.cost).toLocaleString("en-IN")}</span></div>}
            </div>

            <div className="border border-border bg-card p-6" data-testid="pickup-track-timeline">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Progress</div>
              <div className="space-y-0">
                {data.statuses.map((s, i) => {
                  const done = i <= curIdx;
                  const isCur = i === curIdx;
                  return (
                    <div key={s.key} className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        <span className={`h-6 w-6 grid place-items-center rounded-full border-2 ${done ? "bg-emerald-500 border-emerald-500 text-white" : "border-border text-muted-foreground"}`}>
                          {done ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-[10px]">{i + 1}</span>}
                        </span>
                        {i < data.statuses.length - 1 && <span className={`w-0.5 h-8 ${i < curIdx ? "bg-emerald-500" : "bg-border"}`} />}
                      </div>
                      <div className={`pt-0.5 text-sm ${isCur ? "font-bold text-foreground" : done ? "text-foreground" : "text-muted-foreground"}`}>{s.label}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
